import React, { Component } from 'react';
import _ from 'lodash';
import styles from './AnalyticsDropdownContent.module.css';
import clsx from 'clsx';
import Dropdown from 'react-bootstrap/Dropdown';
import { ApplicationState } from 'store/rootReducer';
import {
  createAnalyticsTabItemThunk,
  getMockServicesBySearchCriteria,
  MockServiceSearchCriteria,
  selectTab,
} from 'store/analytics/ui/analyticsUiActions';
import { RouteComponentProps, withRouter } from 'react-router';
import { connect } from 'react-redux';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { MockServiceAnalytics } from 'types/rest/mockServiceTypes';
import NoContentPlaceholder from 'components/ui/noContentPlaceholder/NoContentPlaceholder';
import { WithTranslation, withTranslation } from 'react-i18next';
import BzmSearchInput from 'components/forms/BzmSearchInput/BzmSearchInput';
import MixpanelService from 'utils/mixpanelService';
import { OPEN_ANALYTICS_MOCK_SERVICE } from 'utils/mixpanelEvents';
import BzmDateRangePicker, {
  DateRange,
  FixedTimeRange,
} from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/BzmDateRangePicker';
import AnalyticsLocationDropdown from 'views/Analytics/AnalyticsScrollMenu/AnalyticsDropdown/AnalyticsLocationDropdown';
import CheckBox from 'components/ui/checkBox/CheckBox';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import moment from 'moment';
import { transformMockServiceAnalyticsFromMeta } from 'utils/analyticsUtils';
import MockServiceIcon from 'components/icons/MockServiceIcon';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { MockServiceMetadata } from 'types/rest/analyticsTypes';
import { CraneLocation } from 'types/rest/locationTypes';
import BzmIcon from 'components/icons/BzmIcon';

export type AnalyticsDropdownContentProps = {
  closeDropdownHandler: () => void;
};

type AllProps = DispatchProps & StateProps & RouteComponentProps & WithTranslation & AnalyticsDropdownContentProps;

const searchLabelId = 'search-mock-services-dropdown';
const defaultFromDate = moment()
  .subtract(7, 'days')
  .startOf('day')
  .toDate();
const defaultPageIndex = 0;

/**
 * Content of the dropdown with selection of the virtual service / report.
 */
class AnalyticsDropdownContent extends Component<AllProps> {
  searchInput: React.RefObject<any> = React.createRef();

  state = {
    dateRange: { from: defaultFromDate, to: new Date() } as DateRange,
    fixedTimeRange: null,
    mockServiceSearch: undefined,
    isLoadingMockServices: false,
    selectedLocation: null,
    includeDeleted: true,
    locations: [],
    pageIndex: defaultPageIndex, //Initial fetch index
    mockServices: [],
    totalResults: 0,
  };

  componentDidMount() {
    this.initialMockServiceReload(true);
  }

  componentDidUpdate(prevProps: Readonly<AllProps>) {
    if (this.props.blazeUserConfig !== prevProps.blazeUserConfig) {
      this.initialMockServiceReload(true);
    }
  }

  /**
   * Open Virtual Service
   * @param mockService
   */
  openMockService = (mockService: MockServiceAnalytics) => {
    MixpanelService.track(OPEN_ANALYTICS_MOCK_SERVICE(mockService.id), true);
    this.props.createAnalyticsTabItem(mockService, this.state.dateRange);
    this.props.closeDropdownHandler();
  };

  /**
   * Gets focus on the search input.
   */
  handleFocusOnSearch = () => {
    if (this.searchInput.current != null) {
      setTimeout(() => {
        this.searchInput.current.focus();
      }, 50);
    }
  };

  searchMockServiceHandler = (value: string) => {
    this.setState({ mockServiceSearch: value }, () => {
      this.initialMockServiceReloadDebounced(true);
    });
  };

  /**
   * Represents fresh reload of first chunk of virtual services from the server.
   * Page index needs to be reset in very first fetch due to infinite loader.
   *
   * @param overrideLocations - if true the list of locations will be populated from the current backend response
   */
  initialMockServiceReload = async (overrideLocations: boolean) => {
    const response: BlazeMeterApiResponse = await this.fetchMockServices(true);
    if (response) {
      const mockServiceMetadata: MockServiceMetadata = response.result;
      this.setState({
        mockServices: transformMockServiceAnalyticsFromMeta(mockServiceMetadata),
        locations: overrideLocations ? mockServiceMetadata.allAvailableLocations : this.state.locations,
        pageIndex: defaultPageIndex,
        totalResults: response.total,
      });
    }
  };

  /**
   * When user changes date range, we need to do fresh reload of results.
   * @param dateRange
   */
  dateSelectionChangedHandler = async (dateRange: DateRange) => {
    await this.setState({ dateRange: dateRange });
    this.initialMockServiceReload(true);
  };

  selectLocationHandler = async (location: CraneLocation | null) => {
    await this.setState({ selectedLocation: location });
    this.initialMockServiceReload(false);
  };

  fixedDateChangedHandler = (fixedRange: FixedTimeRange) => {
    this.setState({ fixedTimeRange: fixedRange });
  };

  /** Reloads list of analytics based on the current criteria */
  fetchMockServices = async (isLoaderShown: boolean) => {
    isLoaderShown && this.setState({ isLoadingMockServices: true });

    const criteria: MockServiceSearchCriteria = {
      from: this.state.dateRange.from,
      to: this.state.dateRange.to,
      includeDeleted: this.state.includeDeleted,
      pageIndex: this.state.pageIndex,
      location: this.state.selectedLocation,
      smName: this.state.mockServiceSearch,
    };

    try {
      return await getMockServicesBySearchCriteria(criteria);
    } catch (error) {
      this.props.showToastNotification(
        ToastType.Danger,
        'Cannot get list of virtual services',
        _.get(error, 'response.data.error', error.toString()),
      );
    } finally {
      isLoaderShown && this.setState({ isLoadingMockServices: false });
    }
  };

  initialMockServiceReloadDebounced = _.debounce(this.initialMockServiceReload, 500);

  createDropDownHeaderDom = () => {
    return (
      <h6 className="dropdown-header">
        <BzmIcon variant={'icon-bzm-chart-bar'} className={'mr-2'} />
        {this.props.t('analyticsDropdown.openAnalytics')}
        <a
          style={{ color: 'inherit' }}
          onClick={this.props.closeDropdownHandler}
          className="cursor-pointer float-right"
          aria-label="Close"
          role="button"
        >
          <BzmIcon variant={'icon-bzm-close-remove'}></BzmIcon>
          <span className="sr-only">Close</span>
        </a>
      </h6>
    );
  };

  createLocationDropdownDom = () => {
    return <AnalyticsLocationDropdown locations={this.state.locations} selectLocation={this.selectLocationHandler} />;
  };

  createSearchDom = () => {
    return (
      <>
        <label htmlFor={searchLabelId}>{this.props.t('analyticsDropdown.mockServices.title')}</label>
        <a
          title="Refresh Virtual Services"
          className="hand-cursor ml-2 icon-xs align-middle"
          onClick={() => this.initialMockServiceReload(true)}
        >
          <BzmIcon variant="icon-bzm-sync" className="btn-link btn-no-hover"></BzmIcon>
        </a>
        <BzmSearchInput
          id={'search-mock-services-dropdown'}
          onChange={this.searchMockServiceHandler}
          value={this.state.mockServiceSearch}
          placeholder={this.props.t('analyticsDropdown.mockServices.placeholder')}
          searchRef={this.searchInput}
        />
      </>
    );
  };

  createCheckboxDom = () => {
    return (
      <div className={'mt-4'}>
        <CheckBox
          label={this.props.t('analyticsDropdown.existingMsLabel')}
          checked={this.state.includeDeleted}
          onCheck={() => {
            this.setState({ includeDeleted: !this.state.includeDeleted, pageIndex: defaultPageIndex }, () => {
              this.initialMockServiceReload(false);
            });
          }}
        />
      </div>
    );
  };

  /**
   * Creates list of Virtual Services items.
   * */
  createMockServiceItemsDom = () => {
    // Items are still loading
    if (this.state.mockServices === null || this.state.isLoadingMockServices) {
      return (
        <div className="text-center" style={{ minHeight: '172px' }}>
          {' '}
          <SimpleLoader loadingText="Getting Virtual Services..." style={{ marginTop: '45%' }} />
        </div>
      );
    }

    // Filter by search keyword
    const filteredMockServiceItems = _.cloneDeep(this.state.mockServices);

    // Highlight the searched content
    const dropdownContent: any = filteredMockServiceItems.map((msItem: any) => {
      let name = '' + msItem.name;
      if (this.state.mockServiceSearch !== undefined && this.state.mockServiceSearch !== '') {
        const searchTextRegExp = new RegExp(this.state.mockServiceSearch as any, 'i'); //  case insensitive regexp
        name = name.replace(searchTextRegExp, '<b>$&</b>');
      }

      // Add location name
      const locationName = msItem.meta ? <span className="text-muted ml-3">({msItem.meta.locationName})</span> : '';

      const icon = (
        <span className={styles.icon}>
          <MockServiceIcon type={msItem.meta.type} size={20}></MockServiceIcon>{' '}
        </span>
      );

      return (
        <Dropdown.Item
          className={styles.dropdownItem}
          onClick={() => this.openMockService(msItem)}
          key={msItem.name}
          title={`${msItem.name} (${msItem.meta.locationName})`}
        >
          <span style={{ width: '100%' }}>
            {icon}
            <span dangerouslySetInnerHTML={{ __html: name }}></span>
            {locationName}
          </span>
        </Dropdown.Item>
      );
    });

    // Empty placeholder if we do not have any content available
    if (this.state.mockServices.length === 0) {
      return <NoContentPlaceholder placeHolderContent="No Virtual Services found." height={180} />;
    } else {
      const totalLoaded = this.state.mockServices.length;
      const scrollableDivId = 'scrollableMockServiceList';
      return (
        <>
          {/* Items in scrollbar */}
          <div id={scrollableDivId} className={clsx(styles.dropdownScrollBody)}>
            <InfiniteScroll
              height={totalLoaded >= 8 ? 300 : 'auto'}
              hasMore={totalLoaded < this.state.totalResults}
              scrollableTarget={scrollableDivId}
              dataLength={this.state.mockServices.length}
              loader={
                <div className="text-center">
                  <SimpleLoader loadingText="Getting Virtual Services..."></SimpleLoader>
                </div>
              }
              next={async () => {
                await this.setState({ pageIndex: this.state.pageIndex + 1 });
                const nextMockServicesChunk: BlazeMeterApiResponse | undefined = await this.fetchMockServices(false);
                if (nextMockServicesChunk) {
                  const nextMockServicesList = transformMockServiceAnalyticsFromMeta(nextMockServicesChunk.result);
                  const mockServices = _.concat(this.state.mockServices, nextMockServicesList);
                  this.setState({
                    mockServices: mockServices,
                    totalResults: nextMockServicesChunk.total,
                  });
                }
              }}
            >
              {dropdownContent}
            </InfiniteScroll>
            {/* Items in scrollbar */}
          </div>
        </>
      );
    }
  };

  render() {
    return (
      <div>
        {/*Header*/}
        {this.createDropDownHeaderDom()}
        {/* ./Header*/}

        {/* Body */}
        <div className={'p-4'}>
          <BzmDateRangePicker
            title={'Time Range'}
            dateRange={this.state.dateRange}
            fixedSelection={this.state.fixedTimeRange}
            dateRangeChanged={this.dateSelectionChangedHandler}
            fixedSelectionChanged={this.fixedDateChangedHandler}
          />
          <hr className={styles.divider} />
          {this.createSearchDom()}
          {this.createLocationDropdownDom()}
          {this.createCheckboxDom()}
          {this.createMockServiceItemsDom()}
        </div>
        {/* ./Body */}
      </div>
    );
  }
}

const mapStateToProps = ({ analytics, mockservices, blazeUserConfig }: ApplicationState) => ({
  tabs: analytics.ui.tabs,
  selected: analytics.ui.activeTab,
  mockServices: analytics.ui.mockServices,
  isLoading: analytics.ui.isLoading,
  bzmLocations: mockservices.locData,
  blazeUserConfig: blazeUserConfig.blazeUserConfig,
});

const mapDispatchToProps = {
  selectTab,
  createAnalyticsTabItem: createAnalyticsTabItemThunk,
  showToastNotification,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('analytics')(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(AnalyticsDropdownContent)),
);
